import { first, map } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as _ from 'lodash';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { ThemeConfig } from 'src/assets/data/enum/themeConfig';
import { SAASBRANDNAME } from 'src/assets/data/enum/saasBrandName.enum';
import {
  ConcentrateSubType,
  ForageSubType,
  RationSubType,
  RationTyplogies,
} from 'src/assets/data/enum/rationSubType';
import { Equipment, Product } from '../_models';
import { FarmService } from './farm.service';
import { FieldActivityMix } from '../_models/fieldActivityMix';
import { environment } from '../../../environments/environment';
import { CommodityService } from './commodity.service';
import { AppState } from '../_reducers';
import { selectAllProducts } from '../_selectors/product.selectors';
import { StoreModelService } from './storeModel.service';
import { SM } from '../../../assets/data/enum/storeModel.enum';

export const FORAGES_SUBTYPES: string[] = Object.values(ForageSubType);
export const CONCENTRATES_SUBTYPES: string[] = Object.values(ConcentrateSubType);
export const RATION_TYPOLOGIES: string[] = Object.values(RationTyplogies);

@Injectable()
export class ProductService {
  private apiBaseUrl = environment.apiBaseUrl;
  private productServiceUrl = environment.productServiceUrl;
  private productList: Observable<Product[]>;
  productCountryList$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  currentPreferredProductsCountry: string;
  lastTabSelected = 'ptype3';

  static readonly FERTILIZER_ACTIVITY_EXTRAS_PAC_ITA = [
    'base_materials_substrates',
    'soil_amendments',
  ] as const;

  static getPacItaFertilizerExtras(): string[] {
    return Array.from(ProductService.FERTILIZER_ACTIVITY_EXTRAS_PAC_ITA);
  }

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private farmService: FarmService,
    private commodityService: CommodityService,
    private storeModelService: StoreModelService
  ) {
    this.productList = this.store.pipe(select(selectAllProducts));
    this.setPac();
  }

  setPac() {
    this.farmService.getPreferredProductsCountry().subscribe((pac) => {
      this.currentPreferredProductsCountry = pac;
    });
  }

  getFromLocalStore(): Observable<Product[]> {
    return this.productList;
  }

  getProducts(): Observable<Product[]> {
    const country = this.currentPreferredProductsCountry || 'ITA';
    const productsUrl = `${this.productServiceUrl}api/private/v1/lists/products?preferredProductsCountry=${country}`;
    const xSaas = ThemeConfig.brand || SAASBRANDNAME.XFARM;

    // Set the SaaS brand to receive the correct products from the backend
    const headers = new HttpHeaders({ 'x-saas': xSaas.toLocaleLowerCase() });

    const products$ = this.http.get<Product[]>(productsUrl, { headers });
    const commodities$ = this.commodityService.getCommodities();

    return forkJoin([products$, commodities$]).pipe(
      map(([products, commodities]) => this.setValue(products, commodities))
    );
  }

  setValue(products, commodities): Product[] {
    const currentSaas = environment.themeConfig.brand;
    if (currentSaas == 'ciech') {
      const productsCiech = _.orderBy(
        products,
        (p: any) =>
          (p && p.country == 'ESP') ||
          p.registerNumber == 23917 ||
          p.name == 'KOPPAR FLOW 70' ||
          (p.registerNumber == 'ES-00139' &&
            p.producerName &&
            (p.producerName == 'CIECH-SARZYNA' || p.producerName == 'PROPLAN'))
      );
      products = productsCiech;
    }
    const checkActionYaraProducts = this.storeModelService.enableActionByStoreModelPROAndPermission(
      SM.SM_PRODUCT_YARA
    );
    if (!(checkActionYaraProducts.read && checkActionYaraProducts.purchased)) {
      _.remove(products, (p: any) =>
        _.find(p.externalResources, (er: any) => er.externalSource == 'YARA_CNP')
      );
    }
    products.forEach((element) => {
      element.commodityObj = commodities.find((c) => c.id === element.commodityObj);
      element.nameAndregisterNumber =
        element.name + (element.registerNumber != null ? ' (' + element.registerNumber + ')' : '');
    });
    return products;
  }

  getProduct(id: number, differentPac?: boolean): Observable<Product> {
    let pac = this.currentPreferredProductsCountry;
    if (differentPac) {
      return this.http
        .get<any>(this.productServiceUrl + 'api/private/v1/lists/products/' + id)
        .pipe(map((response) => response));
    } else {
      pac = this.currentPreferredProductsCountry;
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/products/' +
            id +
            '?preferredProductsCountry=' +
            (pac ? pac : 'ITA')
        )
        .pipe(map((response) => response));
    }
  }

  saveProduct(productItem): Observable<Product> {
    const product = { ...productItem };

    delete product.lastUpdated;
    delete product.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    product.companyId = companySelected ? companySelected.id : null;

    if (product.commodityObj && product.commodityObj.id) {
      product.commodityObj = product.commodityObj.id;
    }
    if (product.varietyObj && product.varietyObj.id) {
      product.varietyObj = product.varietyObj.id;
    }

    if (product.id) {
      return this.http
        .put<Product>(
          this.productServiceUrl + 'api/private/v1/lists/products/' + product.id,
          product
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<Product>(this.productServiceUrl + 'api/private/v1/lists/products/', product)
        .pipe(map((response) => response));
    }
  }

  deleteProduct(id: number): Observable<Product> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/lists/products/' + id).pipe(
      map((response) => {
        return { id } as Product;
      })
    );
  }

  getProductById = (id: number): Product => {
    let item: Product;
    this.productList
      .pipe(first((list: Product[]) => list && list.length > 0))
      .subscribe((list: Product[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  getProductByName(name: any) {
    let item: Product;
    this.productList
      .pipe(first((list: Product[]) => list && list.length > 0))
      .subscribe((list: Product[]) => {
        item = list.find((i) => i.name === name);
      });
    return item;
  }

  getProductByName2(name: string): Observable<Product | undefined> {
    return this.productList.pipe(
      first((list) => !!list?.length),
      map((list) => list.find((p) => p.name.trim().toLowerCase() === name.trim().toLowerCase()))
    );
  }

  getIdByProductName(name: any) {
    let product: Product;
    this.productList.subscribe((prods) => {
      product = _.find(
        prods,
        function (f) {
          return f.name === name;
        }.bind(this)
      );
    });
    return product.id;
  }

  getProductByToInputList(): Observable<{ id: number; name: string }[]> {
    return this.productList.pipe(
      map((prods) =>
        prods
          .filter((prod) => prod.companyId !== -2)
          .map((prod) => ({
            id: prod.id,
            name: prod.name + (prod.status ? ' - ( ' + prod.status + ' )' : ''),
          }))
      )
    );
  }

  getProductByActivityType = (activityType) => {
    const productFiltered = [];

    this.productList.subscribe((prods) => {
      _.forEach(prods, (prod) => {
        switch (activityType) {
          case 'seeding':
            if (prod.type === 'seeds') {
              productFiltered.push(prod);
            }
            break;
          case 'transplantation':
            if (prod.type === 'seeds') {
              productFiltered.push(prod);
            }
            break;
          case 'feeding':
            if (prod.type === 'fertilizers') {
              productFiltered.push(prod);
            }
            break;

          case 'defending':
            if (prod.type === 'chemicals' && prod.companyId != -2) {
              productFiltered.push(prod);
            }
            break;

          case 'insemination':
            if (prod.type === 'insemination') {
              productFiltered.push(prod);
            }
            break;

          case 'treatments':
            if (prod.type === 'treatments') {
              productFiltered.push(prod);
            }
            break;

          case 'ration':
            if (prod.type === 'ration') {
              productFiltered.push(prod);
            }
            break;

          default:
            break;
        }
      });
    });
    return productFiltered;
  };

  getAllSubTypeFertilizers = () => {
    return ['mineral', 'organic_solid', 'organic_liquid'];
  };

  getAllSubTypeRation = () => {
    return [
      RationSubType.FIBRES,
      RationSubType.PROTEIN,
      RationSubType.ENERGY,
      RationSubType.HIGH_FIBER,
      RationSubType.HIGH_FIBER_ENERGY,
      RationSubType.HIGH_FIBER_PROTEIN,
      RationSubType.MEDIUM_LOW_FIBER,
      RationSubType.MINERAL,
    ];
  };

  saveAttachmentsToProduct(file): Observable<any> {
    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/lists/productDocuments', file)
      .pipe(map((response) => response));
  }

  getAttachmentsToProduct(): Observable<any> {
    return this.http
      .get<any>(
        this.productServiceUrl +
          'api/private/v1/lists/productDocuments?preferredProductsCountry=' +
          this.currentPreferredProductsCountry
      )
      .pipe(map((response) => response));
  }

  deleteAttachmentsToProduct(file): Observable<Product> {
    return this.http
      .delete(this.apiBaseUrl + 'api/private/v1/lists/productDocuments/' + file.id)
      .pipe(
        map((response) => {
          return { id: file.id } as any;
        })
      );
  }

  sendToEquipment(equipment: Equipment) {
    const companyId = equipment.companyId ? equipment.companyId : null;

    const resource = equipment.externalResources.find(
      (r) => r.externalSource === 'FADE' || r.externalSource === 'CLAAS'
    );
    const { externalSource } = resource;
    const { externalId } = resource;

    const url = `${this.apiBaseUrl}api/private/v3/isoxml/products/${companyId}/send?externalSource=${externalSource}&equipmentId=${externalId}`;
    return this.http.post(url, {});
  }

  getProductCountries() {
    return this.http
      .get<any>(this.productServiceUrl + 'api/private/v1/lists/productCountries')
      .pipe(
        map((response) => {
          if (response) {
            this.productCountryList$.next(response);
            return response;
          }
        })
      );
  }

  static getForagesSubtypes(): string[] {
    return FORAGES_SUBTYPES;
  }
  static getConcentratesSubtypes(): string[] {
    return CONCENTRATES_SUBTYPES;
  }
  static getRationTypologies(): string[] {
    return RATION_TYPOLOGIES;
  }

  //! FIXME performances could be awful for activities having several mixes
  private addProduct(mix: FieldActivityMix) {
    if (mix.product && mix.product.id) {
      mix.product = this.getProductById(mix.product.id);
    }
    return mix;
  }

  getForagesMixes(mixes: FieldActivityMix[]): FieldActivityMix[] {
    const foragesSubtypes = ProductService.getForagesSubtypes();
    return mixes
      .map((mix) => this.addProduct(mix))
      .filter(
        (mix) =>
          mix.product &&
          mix.product.activity &&
          foragesSubtypes.includes(mix.product.activity.valueOf())
      );
  }

  getConcentratesMixes(mixes: FieldActivityMix[]): FieldActivityMix[] {
    const concentratesSubtypes = ProductService.getConcentratesSubtypes();
    return mixes
      .map((mix) => this.addProduct(mix))
      .filter(
        (mix) =>
          mix.product &&
          mix.product.activity &&
          concentratesSubtypes.includes(mix.product.activity.valueOf())
      );
  }

  getMixesBySubtype(mixes: FieldActivityMix[], subtype: string): FieldActivityMix[] {
    return mixes
      .map((mix) => this.addProduct(mix))
      .filter(
        (mix) => mix.product && mix.product.activity && mix.product.activity.valueOf() === subtype
      );
  }

  getForagesConcentratesRatio(mixes: FieldActivityMix[]): number {
    const forages = this.getForagesMixes(mixes);
    const concentrates = this.getConcentratesMixes(mixes);

    let foragesDrySubstance = 0;
    let concentratesDrySubstance = 0;

    forages.forEach((mix) => {
      foragesDrySubstance += ProductService.getDrySubstanceQuantity(mix);
    });

    concentrates.forEach((mix) => {
      concentratesDrySubstance += ProductService.getDrySubstanceQuantity(mix);
    });
    return concentratesDrySubstance ? foragesDrySubstance / concentratesDrySubstance : 0;
  }

  getMolassesDrySubstanceRatio(mixes: FieldActivityMix[]): number {
    const forages = this.getForagesMixes(mixes);
    const concentrates = this.getConcentratesMixes(mixes);
    const molasses = this.getMixesBySubtype(mixes, 'molasses');

    let allDrySubstance = 0;
    let molassesDrySubstance = 0;

    forages.forEach((mix) => {
      allDrySubstance += ProductService.getDrySubstanceQuantity(mix);
    });

    concentrates.forEach((mix) => {
      allDrySubstance += ProductService.getDrySubstanceQuantity(mix);
    });

    molasses.forEach((mix) => {
      molassesDrySubstance += ProductService.getDrySubstanceQuantity(mix);
    });

    return allDrySubstance ? molassesDrySubstance / allDrySubstance : 0;
  }

  static getDrySubstanceQuantity(mix: FieldActivityMix): number {
    let drySubstanceQuantity = 0;
    if (mix.product && mix.doseUsed && mix.drySubstancePercentage) {
      drySubstanceQuantity = mix.doseUsed.valueOf() * (mix.drySubstancePercentage / 100);
    }
    return drySubstanceQuantity;
  }

  verifyStatusRegistered(status): boolean {
    const licensedList = [
      'Autorizzato',
      'REG',
      'ELU',
      'Registered',
      'Registrato',
      'Homologué',
      'Registrado',
    ];
    return status !== null && status !== undefined && licensedList.indexOf(status) > -1;
  }

  getFertilizerCategoryBase(): string[] {
    return ['mineral', 'organic_solid', 'organic_liquid'];
  }
}
